import { isEmpty } from 'lodash-es'
import { isAvailableNow } from '@tictuk/item-availability'

import Home from 'mobx/Home'
import Infra from 'mobx/Infra'

import { getStoreInfo } from 'mobx/AddressManager/utils'
import { initSessionAndLoadMenu, validateAddress, convertGooglePlaceObjToServerObj } from 'utils/utils'
import loadMenu from 'utils/api/loadMenu/loadMenu'
import { initOrUpdateSession } from 'mobx/Session/utils'
import { webFlowAddress } from 'utils/api/webFlowAddress'

import { staticImplements } from 'utils/types'

import type { LocalizationDependencies, Address, LocalizationFlow, Configuration } from './types'
import type CartStore from 'mobx/Cart/store'
import { type AppParams } from 'mobx/Infra/Infra.type'
import type { NextRouter } from 'next/router'
import type { Store } from 'types/StoreContext'
import type AddressManagerV2Store from 'mobx/AddressManagerV2/store'
import type { StoreFrontResponse } from 'mobx/AddressManager/types';
import { StoreStatus } from 'mobx/AddressManager/types'
import LocalizationError, { ErrorCode } from 'mobx/AddressManager/errors'

@staticImplements<LocalizationDependencies>()
export default class LocalizationRepository {

    static cartStore: CartStore
    
    static setStore: (store: Store) => void

    static router: NextRouter

    static addressManagerV2Store: AddressManagerV2Store | undefined

    static deliveryEnabled: () => boolean

    static pickupEnabled: () => boolean

    static appParams: AppParams | null = null

    static async getStores(formatted: string, lat: number, lng: number) {

        const { l: lang, c: chainId } = this.appParams!

        const res = await webFlowAddress.getBranchesList({
            formatted,
            lat,
            lng,
            chainId,
            lang,
            orderType: null,
            showLoader: false
        })

        return res.msg || []
    }

    static setAppParams(appParams: AppParams): void {
		this.appParams = appParams
	}

    static setCartStore(cartStore: CartStore): void {
		this.cartStore = cartStore
	}

    static setSetStore(setStore: (store: Store) => void) {
        this.setStore = setStore
    }

    static setRouter(router: NextRouter) {
        this.router = router
    }

    static setAddressManagerV2Store(addressManagerV2Store: AddressManagerV2Store) {
        this.addressManagerV2Store = addressManagerV2Store
    }

    static setDeliveryEnabled(deliveryEnabled: () => boolean) {
        this.deliveryEnabled = deliveryEnabled
    }

    static setPickupEnabled(pickupEnabled: () => boolean) {
        this.pickupEnabled = pickupEnabled
    }

    static save(flow: LocalizationFlow) {
        localStorage.setItem('localizationFlow', JSON.stringify(flow))
    }

    static get(): LocalizationFlow | null {
        if(typeof window !== 'undefined') {
            const data = localStorage?.getItem('localizationFlow')
            if(data) {
                return JSON.parse(data) as LocalizationFlow
            }
        }

        return null
    }

    static config(): Configuration | undefined {
        return this.appParams?.features?.localizationFlow
    }

    static async initSessionAndLoadMenu(storeId: string) {
        return initSessionAndLoadMenu(loadMenu)({ isGeneratedStatically: false, storeId, router: this.router, setStore: this.setStore, Home, cartStore: this.cartStore, addressManagerV2Store: this.addressManagerV2Store })
    }

    static async initSession(storeId: string | undefined) {
        return initOrUpdateSession({storeId})
    }

    static async getValidateAddress(address: Address): Promise<any> {
        const googlePlacesObjToConvert = {
            formatted_address: address.formatted,
            geometry: {location: {lat: address.location.lat, lng: address.location.lng}},
            types: [],
            address_components: address.components
        }

        const convertedAddressObj = convertGooglePlaceObjToServerObj(googlePlacesObjToConvert)
		const validateAddressResponse = await validateAddress(convertedAddressObj, false)

		return validateAddressResponse
	}

    static async goToLocalizedMenu(): Promise<void> {
        const menuUrl = localStorage?.getItem('menuUrl')
        if(menuUrl) {
            await this.router.push(`/menu${menuUrl}`)
        }
    }

    static getLocalizedMenuPath(): string | undefined {
        const menuUrl = localStorage?.getItem('menuUrl')
        if(menuUrl) {
            return `/menu${menuUrl}`
        }
    }

    static async getStoreDetailsById(storeId: string): Promise<StoreFrontResponse> {
		return getStoreInfo(storeId, Infra)
    }

    static async checkPickupAvailabilityByStore(store: StoreFrontResponse) {

		if (store.status !== StoreStatus.Active) {
			throw new LocalizationError('Store is Shut Down', ErrorCode.STORE_CLOSED)
		}

		if (!store.availability) {
			throw new LocalizationError('Store is missing availability', ErrorCode.STORE_CLOSED)
		}

		const storeAvailable = isAvailableNow(store.availability)

		if (!storeAvailable) {
			throw new LocalizationError('Store is closed', ErrorCode.STORE_CLOSED)
		}

		if (!store.orderTypes.peakup.enabled) {
			throw new LocalizationError(`Pickup is disabled for store ${store.id}`, ErrorCode.PICKUP_DISABLED_FOR_STORE)
		}

		if (isEmpty(store.location)) {
			throw new LocalizationError(`Pickup is disabled for store ${store.id}`, ErrorCode.STORE_CLOSED)
		}
	}
}